'use client';

import { useTranslations } from 'next-intl';
import { type ComponentProps, useState } from 'react';
import { cn } from '../../../shadcn';
import type { MagazineArticle } from '../../helpers/magazineCategory';
import type { ArticleSidebarTab } from '../types';
import { ArticleSidebarItem } from './ArticleSidebarItem';
import { ArticleSidebarTabList } from './ArticleSidebarTabList';

type FeaturedArticlesContentProps = Omit<ComponentProps<'div'>, 'children'> & {
  latestArticles: MagazineArticle[];
  recommendedArticles: MagazineArticle[];
  hasError: boolean;
  'data-testid'?: string;
};

const TABS: Array<{ id: ArticleSidebarTab; labelKey: 'tabs.latest' | 'tabs.recommended'; trackName: string }> = [
  { id: 'latest', labelKey: 'tabs.latest', trackName: 'Latest Articles' },
  // TODO: WEBD-621 re-enable recomended articles post MVP
  // { id: 'recommended', labelKey: 'tabs.recommended', trackName: 'Recommended' },
];

type FeaturedArticleRowProps = {
  article: MagazineArticle;
  index: number;
  showDivider: boolean;
  'data-testid': string;
};

function FeaturedArticleRow({ article, index, showDivider, 'data-testid': dataTestId }: FeaturedArticleRowProps) {
  return (
    <div className={cn(showDivider && 'border-b border-dashed border-border')}>
      <ArticleSidebarItem
        index={index}
        title={article.title}
        category={article.category?.title}
        publishedDate={article.publishedDate}
        href={article.seoInfo.canonicalUrl}
        data-testid={dataTestId}
      />
    </div>
  );
}

function FeaturedArticlesContent({
  latestArticles,
  recommendedArticles,
  hasError,
  className,
  'data-testid': dataTestId = 'featured-articles',
  ...props
}: FeaturedArticlesContentProps) {
  const t = useTranslations('magazine.featured_articles');
  const [selectedTab, setSelectedTab] = useState<ArticleSidebarTab>('latest');

  const tabs = TABS.map(tab => ({ id: tab.id, label: t(tab.labelKey), trackName: tab.trackName }));
  const selectedTabTrackName = tabs.find(tab => tab.id === selectedTab)?.trackName;
  const articles = selectedTab === 'latest' ? latestArticles : recommendedArticles;

  return (
    <div className={cn('flex w-full flex-col gap-6 pt-4', className)} data-testid={dataTestId} data-track-container="Featured Articles" {...props}>
      <ArticleSidebarTabList tabs={tabs} selectedTab={selectedTab} onTabChange={setSelectedTab} ariaLabel={t('tabs.aria_label')} data-testid={dataTestId} />

      <div className="flex w-full flex-col border-y border-border" data-testid={`${dataTestId}-articles`} data-track-container={selectedTabTrackName}>
        {/* Interim copy for API edge cases until dedicated empty/error designs are confirmed. */}
        {hasError && (
          <p className="px-2 py-6 text-sm text-foreground-subtle" data-testid={`${dataTestId}-error`}>
            {t('states.error')}
          </p>
        )}

        {!hasError && articles.length === 0 && (
          <p className="px-2 py-6 text-sm text-foreground-subtle" data-testid={`${dataTestId}-empty`}>
            {t('states.empty')}
          </p>
        )}

        {!hasError &&
          articles.map((article, index) => (
            <FeaturedArticleRow key={article.id} article={article} index={index + 1} showDivider={index < articles.length - 1} data-testid={`${dataTestId}-article-${index + 1}`} />
          ))}
      </div>
    </div>
  );
}

export { FeaturedArticlesContent, type FeaturedArticlesContentProps };
